import React from 'react';

/**
 * NumberField - Numeric input field with optional min/max/step
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {number} [props.field.min] - Minimum value
 * @param {number} [props.field.max] - Maximum value
 * @param {number} [props.field.step] - Step increment (default: 1)
 * @param {number} [props.field.default] - Default value
 * @param {string} [props.field.suffix] - Suffix to display (e.g., 'px', '%')
 * @param {number} props.value - Current value
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 * @param {boolean} [props.compact] - Use compact inline layout
 */
export default function NumberField({ field, value, onChange, disabled = false, error, compact = false }) {
  const handleChange = (e) => {
    const newValue = e.target.value === '' ? '' : parseFloat(e.target.value);
    onChange(isNaN(newValue) ? '' : newValue);
  };

  const inputElement = (
    <div className="relative">
      <input
        type="number"
        value={value ?? field.default ?? ''}
        onChange={handleChange}
        disabled={disabled}
        min={field.min}
        max={field.max}
        step={field.step || 1}
        className={`w-full px-2 py-1 text-xs border rounded bg-gray-50 ${
          field.suffix ? 'pr-8' : ''
        } ${
          error
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400'
        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      />
      {field.suffix && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
          {field.suffix}
        </span>
      )}
    </div>
  );

  // Compact mode: inline label + control
  if (compact) {
    return (
      <div className="flex items-center gap-2 py-0.5">
        <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
          {field.label}
        </label>
        <div className="flex-1 min-w-0">
          {inputElement}
        </div>
      </div>
    );
  }

  // Standard mode: stacked layout
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {field.label}
      </label>
      {inputElement}
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
